import React, { useEffect, useState } from "react";
import { fmtDateTime } from "@/lib/dateUtils";
import { Link, useNavigate } from "react-router-dom";
import { openMeetTracked } from "@/lib/meetWindow";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CalendarClock, Sparkles, MessagesSquare, ScrollText, ArrowUpRight, Award, Trophy } from "lucide-react";
import { BookShelfStrip } from "@/pages/BookShelf";
import SnapshotTimeline from "@/components/SnapshotTimeline";

export default function StudentDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [classes, setClasses] = useState([]);
  const [quizzes, setQuizzes] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const c = await api.get("/classes", { params: { upcoming: true } });
        setClasses(c.data.classes || []);
        const q = await api.get("/ai/quiz");
        setQuizzes(q.data.quizzes || []);
        const b = await api.get("/badges");
        setBadges(b.data.badges || []);
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="student-dashboard">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ student overview</div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl tracking-tighter">Welcome, {user?.full_name?.split(" ")[0]}.</h1>
        <p className="text-sm text-neutral-600 mt-1">Your classes, quizzes and 24/7 AI tutor — all in one place.</p>
      </div>

      <BookShelfStrip />

      <div className="grid lg:grid-cols-3 gap-6">
        <Card title="Upcoming classes" link="/student/classes">
          {loading ? <Loading /> : classes.length === 0 ? <Empty msg="No classes scheduled." /> : classes.slice(0, 5).map((c) => (
            <Row key={c.id}>
              <div>
                <div className="font-medium text-sm">{c.title}</div>
                <div className="text-xs text-neutral-500">{c.subject} · {fmtDateTime(c.start_time)}</div>
              </div>
              <div className="flex items-center gap-1.5">
                {c.meet_url && (
                  <button
                    onClick={() => { openMeetTracked(c.meet_url, null, c.id); nav(`/live/${c.id}`); }}
                    data-testid={`student-join-${c.id}`}
                    className="text-xs font-semibold bg-[#00897B] text-white hover:bg-[#00A48F] px-3 py-1.5 rounded-md"
                    title="Join the Google Meet video call (opens in a new tab) — the classroom stays here for the board & quizzes"
                  >Join</button>
                )}
                <Link to={`/live/${c.id}`} data-testid={`student-classroom-${c.id}`} className="text-xs font-semibold border border-neutral-300 hover:border-[#0A0C10] px-3 py-1.5" title="Open the classroom hub — board, quizzes and activities">
                  {c.meet_url ? "Classroom" : "Join"}
                </Link>
              </div>
            </Row>
          ))}
        </Card>

        <Card title="Your quizzes" link="/student/quizzes">
          {loading ? <Loading /> : quizzes.length === 0 ? <Empty msg="No quizzes assigned." /> : quizzes.slice(0, 5).map((q) => (
            <Row key={q.id}>
              <div>
                <div className="font-medium text-sm">{q.title}</div>
                <div className="text-xs text-neutral-500">{q.subject} · {q.questions?.length} questions</div>
              </div>
            </Row>
          ))}
        </Card>

        <div className="bg-[#0A0C10] text-white p-6 noise-overlay">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-400 mb-2">/ ai tutor</div>
          <h2 className="font-display font-bold text-2xl tracking-tighter">Stuck on something?</h2>
          <p className="text-sm text-neutral-300 mt-2">Ask your AI tutor — homework help, topic explanations, exam prep.</p>
          <div className="mt-5 text-xs text-neutral-400 flex items-center gap-2"><Sparkles size={12} className="text-[#FFD600]" /> {user?.ai_credits} credits available</div>
          <Link to="/student/ai-tutor" data-testid="open-ai-tutor" className="mt-4 inline-flex items-center gap-2 bg-[#FFD600] text-[#0A0C10] font-bold px-5 py-2.5 btn-brutal-shadow">
            <MessagesSquare size={14} /> Open AI Tutor
          </Link>
        </div>
      </div>

      {/* Handwritten work timeline — AI Snapshots from live classes */}
      {user?.id && <SnapshotTimeline studentId={user.id} title="Your handwritten work" />}

      {/* Badges wall */}
      <div className="bg-gradient-to-br from-[#0A0C10] via-[#1A0F2E] to-[#10141C] text-white p-6 lg:p-8 noise-overlay" data-testid="badges-wall">
        <div className="flex items-center justify-between gap-4 flex-wrap mb-5">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#FFD600] font-bold inline-flex items-center gap-1.5">
              <Award size={12} /> Earned badges
            </div>
            <h2 className="font-display font-bold text-2xl lg:text-3xl tracking-tighter mt-1">{badges.length === 0 ? "Earn your first badge" : `${badges.length} badges unlocked`}</h2>
            <p className="text-sm text-neutral-400 mt-1">{badges.length === 0 ? "Score 80%+ on any quiz to unlock your first." : "Keep going — there's always a new tier above."}</p>
          </div>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-md">
            <Trophy size={14} className="text-[#FFD600]" />
            <div className="text-[10px] font-mono uppercase tracking-wider text-white/70">Total · <span className="text-white font-bold">{badges.length}</span></div>
          </div>
        </div>
        {badges.length === 0 ? (
          <div className="text-center py-8 text-neutral-500 text-sm border border-dashed border-white/10 rounded-md">No badges yet — your first quiz win unlocks one!</div>
        ) : (
          <div className="grid sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {badges.slice(0, 15).map((b) => (
              <div key={b.id} className="bg-white/[0.04] border border-white/10 hover:border-white/30 rounded-md p-3 text-center transition-colors" data-testid={`badge-${b.badge_id}`}>
                <div className="w-12 h-12 mx-auto rounded-full flex items-center justify-center mb-2"
                  style={{ background: `${b.color}22`, color: b.color, border: `2px solid ${b.color}55` }}>
                  <Trophy size={20} />
                </div>
                <div className="font-display font-bold text-sm tracking-tight">{b.title}</div>
                <div className="text-[10px] text-neutral-400 mt-1 line-clamp-2">{b.desc}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ title, link, children }) {
  return (
    <div className="bg-white border border-neutral-200 p-6">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display font-semibold">{title}</div>
        {link && <Link to={link} className="text-xs text-neutral-500 hover:text-[#0A0C10] inline-flex items-center gap-1">All <ArrowUpRight size={12} /></Link>}
      </div>
      {children}
    </div>
  );
}
function Row({ children }) { return <div className="flex items-center justify-between py-3 border-b border-neutral-100 last:border-0">{children}</div>; }
function Empty({ msg }) { return <div className="text-sm text-neutral-500 py-6 text-center border border-dashed border-neutral-200">{msg}</div>; }
function Loading() { return <div className="text-sm text-neutral-500 py-6 text-center">Loading…</div>; }
